"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _componentsWeb = require("@telus-uds/components-web");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _constants = require("../constants");
var _utils = require("../utils");
var _contexts = require("../contexts");
var _FieldRenderer = _interopRequireDefault(require("../FieldRenderer"));
var _Spinner = _interopRequireDefault(require("../Spinner"));
var _SubmitButton = _interopRequireDefault(require("../SubmitButton"));
var _SpinnerErrorMessage = _interopRequireDefault(require("../SpinnerErrorMessage"));
var _SubmitMessage = _interopRequireDefault(require("../SubmitMessage"));
var _Recaptcha = _interopRequireDefault(require("../Recaptcha"));
var _FieldGroup = _interopRequireDefault(require("../FieldGroup"));
var _ConsentField = _interopRequireDefault(require("../ConsentField"));
var _StepTracker = _interopRequireDefault(require("../StepTracker"));
var _useFormScrollRef = _interopRequireDefault(require("../hooks/useFormScrollRef"));
var _useSubmitForm = _interopRequireDefault(require("../hooks/useSubmitForm"));
var _useGetForm = _interopRequireDefault(require("../hooks/useGetForm"));
var _ErrorBoundary = _interopRequireDefault(require("../utils/ErrorBoundary"));
var _FormBuilderFeedback = _interopRequireDefault(require("../FormBuilderFeedback"));
var _ThemeContext = require("../contexts/ThemeContext");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function _getRequireWildcardCache(e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
var isFieldGroup = field => field.type === _constants.FIELD_TYPES.FIELD_GROUP_FIELD;
var isStepTracker = field => field.type === _constants.FIELD_TYPES.STEP_TRACKER_FIELD;

// get available cookies with values
var cookies = (0, _utils.getCookies)();
var ConditionalWrap = _ref => {
  var {
    condition,
    wrap,
    children
  } = _ref;
  return condition ? wrap(children) : children;
};
var FormBuilder = props => {
  var {
    spaceID,
    environment = undefined,
    entryID,
    usePreviewAPI = false,
    bypassCache = false,
    isProduction = true,
    accessToken,
    accessConfig,
    onSubmit = undefined,
    hasCustomPostSubmitMessage,
    hasUnsuccessfulPostSubmitMessage,
    preFillValues = {},
    showSummary = 'No',
    theme = 'Allium',
    showCard = 'No',
    // used for storybook testing of spacing prop since this prop is only available at form level
    testSpacing,
    testFieldGroupLeadingSpace,
    testFieldGroupTrailingSpace,
    validationApiConfig = {}
  } = props;
  var locale = (0, _react.useMemo)(() => props.locale || _constants.CONTENTFUL_LOCALE.enCA, [props.locale]);
  var language = (0, _react.useMemo)(() => (0, _utils.getLanguage)(locale), [locale]);
  var preFillValuesWithCookies = _objectSpread(_objectSpread({}, preFillValues), cookies);
  var [userGaveConsent, setUserGaveConsent] = (0, _react.useState)(false);
  var [error, setError] = (0, _react.useState)(null);
  var [errorApiValidationMessage, setErrorApiValidationMessage] = (0, _react.useState)(null);

  // This reference is used on the consent checkbox, when submitting to
  // set focus if not selected when required
  // const consentRef = createRef()  // Fallback in case issues with useRef are found
  var consentRef = (0, _react.useRef)();
  var {
    fieldValues,
    fieldReferences,
    consentField,
    formMetaData,
    fieldData,
    showErrorFields
  } = (0, _useGetForm.default)(spaceID, environment, entryID, locale, usePreviewAPI, bypassCache, isProduction, accessToken, accessConfig, language, preFillValuesWithCookies, setError);
  var {
    submitForm: _submitForm,
    clickedSubmit,
    readyForSubmission,
    hasSubmitted,
    backendErrored,
    submissionID
  } = (0, _useSubmitForm.default)(fieldValues, userGaveConsent, consentField, consentRef, formMetaData, onSubmit, setError, isProduction, fieldReferences, language, fieldData, accessConfig, usePreviewAPI, validationApiConfig, setErrorApiValidationMessage);

  // This reference will be used on submit to scroll to the top of the form
  // const scrollRef = createRef() // Fallback in case issues with useRef are found
  // const scrollRef = useRef()
  /** Check if BlockFormBuilder in Site Builder has any custom post submission message
  It will replace Form and message with BlockRow so no need to Scroll here.
  then Scroll will be handled by Site Builder */
  var hasBlockFormBuilderPostSubmitMessage = hasCustomPostSubmitMessage || hasUnsuccessfulPostSubmitMessage;
  var scrollRef = (0, _useFormScrollRef.default)(hasSubmitted, hasBlockFormBuilderPostSubmitMessage);
  var spacing = testSpacing || (formMetaData === null || formMetaData === void 0 ? void 0 : formMetaData.spacing);
  var onConsentChange = isValid => {
    setUserGaveConsent(isValid);
  };
  var renderField = field => {
    // Render FieldGroup here, outside of FieldRenderer to avoid circular reference
    // between FieldGroup and FieldRenderer
    if (isFieldGroup(field)) {
      return /*#__PURE__*/_react.default.createElement(_FieldGroup.default, {
        key: "field-group-".concat(field.id),
        heading: field.heading,
        subheading: field.subheading,
        fields: field.fields,
        fieldValues: fieldValues,
        preFillValues: preFillValuesWithCookies,
        language: language,
        mobileWidth: field.mobileWidth,
        tabletWidth: field.tabletWidth,
        desktopWidth: field.desktopWidth,
        clickedSubmit: clickedSubmit,
        fieldReferences: fieldReferences,
        fieldData: fieldData,
        spacing: spacing,
        removeSpacing: field.removeSpacing,
        isProduction: isProduction,
        environment: environment,
        leadingSpace: field.leadingSpace,
        trailingSpace: field.trailingSpace
      });
    } else if (isStepTracker(field)) {
      return /*#__PURE__*/_react.default.createElement(_StepTracker.default, {
        key: "step-tracker-".concat(field.id),
        steps: field.steps,
        fieldValues: fieldValues,
        formMetaData: formMetaData,
        preFillValues: preFillValuesWithCookies,
        language: language,
        clickedSubmit: clickedSubmit,
        fieldReferences: fieldReferences,
        fieldData: fieldData,
        isProduction: isProduction,
        environment: environment,
        submitForm: _submitForm,
        continueButtonLabel: field.continueButtonLabel,
        submitButtonLabel: field.submitButtonLabel
      });
    } else {
      return _FieldRenderer.default.renderField(field, fieldValues, preFillValuesWithCookies, language, clickedSubmit, fieldReferences[field.id], fieldData, fieldReferences, isProduction, environment, spacing);
    }
  };
  var isStepTrackerForm = formMetaData.fields.some(field => isStepTracker(field));
  if (isStepTrackerForm) {
    var {
      error: _error,
      message
    } = (0, _utils.checkStepTrackerConfigError)(formMetaData, consentField, showSummary);
    if (_error) {
      return /*#__PURE__*/_react.default.createElement(_FormBuilderFeedback.default, {
        theme: theme,
        validation: "error",
        title: "Form Builder"
      }, message);
    }
  }
  var fields = formMetaData.fields.map(renderField).filter(field => field !== undefined);

  // Add the consent field for rendering
  if (consentField !== undefined && !showErrorFields) {
    var {
      legalTemplate,
      consentOptions,
      customErrorMessage,
      isReadOnly
    } = consentField;
    fields.push(/*#__PURE__*/_react.default.createElement(_componentsWeb.FlexGrid.Col, {
      key: "consent-field",
      xs: 12
    }, /*#__PURE__*/_react.default.createElement(_ConsentField.default, {
      legalTemplate: legalTemplate,
      options: consentOptions,
      language: language,
      onChange: onConsentChange,
      clickedSubmit: clickedSubmit,
      inputRef: consentRef,
      errorMessage: customErrorMessage,
      isReadOnly: isReadOnly || false
    })));
  }
  var bottomSpacingValue = 7;
  return /*#__PURE__*/_react.default.createElement(_ThemeContext.FormBuilderThemeProvider, {
    theme: theme
  }, /*#__PURE__*/_react.default.createElement(_contexts.StorybookContext.Provider, {
    value: {
      testSpacing,
      testFieldGroupLeadingSpace,
      testFieldGroupTrailingSpace
    }
  }, /*#__PURE__*/_react.default.createElement(_contexts.AppContext.Provider, {
    value: {
      spacing
    }
  }, /*#__PURE__*/_react.default.createElement(_ErrorBoundary.default, null, /*#__PURE__*/_react.default.createElement(_componentsWeb.Box, {
    bottom: bottomSpacingValue
  }, /*#__PURE__*/_react.default.createElement(ConditionalWrap, {
    condition: (formMetaData === null || formMetaData === void 0 ? void 0 : formMetaData.showCard) === 'Yes' || showCard === 'Yes',
    wrap: wrappedChildren => /*#__PURE__*/_react.default.createElement(_componentsWeb.Card, null, wrappedChildren)
  }, /*#__PURE__*/_react.default.createElement(_componentsWeb.FlexGrid, {
    outsideGutter: false
  }, /*#__PURE__*/_react.default.createElement(_componentsWeb.FlexGrid.Row, null, /*#__PURE__*/_react.default.createElement(_componentsWeb.FlexGrid.Col, {
    xs: 12
  }, /*#__PURE__*/_react.default.createElement("div", {
    ref: scrollRef
  }), error && /*#__PURE__*/_react.default.createElement(_SpinnerErrorMessage.default, {
    error: error
  }), !error && hasSubmitted && /*#__PURE__*/_react.default.createElement(_SubmitMessage.default, {
    backendErrored: backendErrored,
    formMetaData: formMetaData,
    fields: fields,
    showSummary: showSummary,
    error: error,
    hasSubmitted: hasSubmitted,
    language: language,
    locale: locale,
    submissionID: submissionID,
    successfulSubmitBlock: hasCustomPostSubmitMessage,
    unsuccessfulSubmitBlock: hasUnsuccessfulPostSubmitMessage
  })), !error && !hasSubmitted && fields, /*#__PURE__*/_react.default.createElement(_componentsWeb.FlexGrid.Col, {
    xs: 12
  }, !error && !hasSubmitted && formMetaData.enableRecaptcha && /*#__PURE__*/_react.default.createElement(_Recaptcha.default, {
    language: language
  }), errorApiValidationMessage && /*#__PURE__*/_react.default.createElement(_componentsWeb.Box, null, /*#__PURE__*/_react.default.createElement(_componentsWeb.Typography, {
    block: true,
    variant: {
      size: 'h4',
      colour: 'danger'
    }
  }, errorApiValidationMessage)), !error && !showErrorFields && formMetaData.fields.length !== 0 && !readyForSubmission && !isStepTrackerForm && /*#__PURE__*/_react.default.createElement(_SubmitButton.default, {
    submitForm: () => _submitForm(fieldData),
    formMetaData: formMetaData
  }), !error && readyForSubmission && !hasSubmitted && /*#__PURE__*/_react.default.createElement(_Spinner.default, {
    locale: locale,
    language: language
  }))))))))));
};
FormBuilder.propTypes = {
  locale: _propTypes.default.string.isRequired,
  accessToken: _propTypes.default.string,
  accessConfig: _propTypes.default.string,
  spaceID: _propTypes.default.string.isRequired,
  environment: _propTypes.default.string,
  entryID: _propTypes.default.string.isRequired,
  usePreviewAPI: _propTypes.default.bool,
  bypassCache: _propTypes.default.bool,
  isProduction: _propTypes.default.bool,
  onSubmit: _propTypes.default.func,
  preFillValues: _propTypes.default.object,
  showSummary: _propTypes.default.oneOf(['Yes', 'No']),
  showCard: _propTypes.default.oneOf(['Yes', 'No']),
  theme: _propTypes.default.oneOf(_ThemeContext.AVAILABLE_THEMES),
  // used for storybook testing of spacing prop since this prop is only available at form level
  testSpacing: _propTypes.default.oneOf(_constants.FORM_SPACING_OPTIONS),
  testFieldGroupLeadingSpace: _propTypes.default.oneOf(_constants.UDS_SPACING_OPTIONS),
  testFieldGroupTrailingSpace: _propTypes.default.oneOf(_constants.UDS_SPACING_OPTIONS),
  validationApiConfig: _propTypes.default.object
};

// Globally exported component should be identifiable
FormBuilder.displayName = 'FormBuilder';
var _default = exports.default = FormBuilder;